import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  courseCategoryImageUrl,
  createCourseCategory,
  deleteCourseCategory,
  listCourseCategories,
  removeCourseCategoryImage,
  updateCourseCategory,
  uploadCourseCategoryImage,
  type CourseCategory,
} from '../lib/creators-api';

/** Radix forbids an empty `SelectItem` value, so "top level" needs a sentinel. */
const NO_PARENT = '__none__';

/**
 * Course topics — the LMS's own category registry, deliberately SEPARATE from
 * product categories (a course's topic lives on `courses.category_id`, never
 * on its product). Drives the `/courses` topic chips + the learning header
 * band, and the topic picker in the creator portal. Hidden topics stay on
 * their courses but drop out of the public list; deleting one un-tags its
 * courses (SET NULL) — nothing is unpublished.
 *
 * Topics nest exactly ONE level. The server owns that rule; this page keeps an
 * admin out of it by only ever offering ROOT topics as a parent.
 */
export function CourseCategoriesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-course-categories'], queryFn: listCourseCategories });
  const [name, setName] = useState('');
  const [newParentId, setNewParentId] = useState<string>(NO_PARENT);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-course-categories'] });

  const create = useMutation({
    mutationFn: createCourseCategory,
    meta: { successMessage: 'Topic created.' },
    onSuccess: () => {
      invalidate();
      setName('');
      setNewParentId(NO_PARENT);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CourseCategory> }) =>
      updateCourseCategory(id, patch),
    meta: { successMessage: 'Topic updated.' },
    onSuccess: invalidate,
  });
  const setImage = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadCourseCategoryImage(id, file),
    meta: { successMessage: 'Picture updated.' },
    onSuccess: invalidate,
  });
  const clearImage = useMutation({
    mutationFn: removeCourseCategoryImage,
    meta: { successMessage: 'Picture removed.' },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: deleteCourseCategory,
    meta: { successMessage: 'Topic deleted.' },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['admin-courses'] });
    },
  });
  const cats = q.data ?? [];
  const roots = cats.filter((c) => c.parentId == null);

  // The API returns one flat sort-ordered list; nesting is presentational.
  // Each entry keeps its own SIBLING group so the arrows reorder WITHIN a
  // level — swapping a child past its parent's boundary would just look broken.
  const rows: Array<{ cat: CourseCategory; depth: 0 | 1; siblings: CourseCategory[] }> = [];
  for (const root of roots) {
    rows.push({ cat: root, depth: 0, siblings: roots });
    const kids = cats.filter((c) => c.parentId === root.id);
    for (const kid of kids) rows.push({ cat: kid, depth: 1, siblings: kids });
  }

  const move = (siblings: CourseCategory[], cat: CourseCategory, dir: -1 | 1) => {
    const i = siblings.indexOf(cat);
    const a = siblings[i];
    const b = siblings[i + dir];
    if (!a || !b) return;
    // Swap sort orders; when two rows share one, nudge so the move sticks.
    const aOrder = a.sortOrder === b.sortOrder ? b.sortOrder + (dir === -1 ? -1 : 1) : b.sortOrder;
    update.mutate({ id: a.id, patch: { sortOrder: Math.max(0, aOrder) } });
    if (a.sortOrder !== b.sortOrder) update.mutate({ id: b.id, patch: { sortOrder: a.sortOrder } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Course topics</h1>
        <p className="text-sm text-muted-foreground mt-1">
          The topics learners filter courses by on the learning home, and the list creators pick
          from when they publish. Separate from product categories — a course&apos;s topic lives on
          the course, not the shop catalog. A topic can sit under one top-level topic; sub-topics
          can&apos;t be nested further.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Topics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              create.mutate({
                name: name.trim(),
                sortOrder: cats.length,
                parentId: newParentId === NO_PARENT ? null : newParentId,
              });
            }}
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="New topic (e.g. Trading, Marketing, Personal Development)"
              className="max-w-sm"
            />
            <ParentSelect
              value={newParentId}
              roots={roots}
              onChange={setNewParentId}
              ariaLabel="Parent topic for the new topic"
            />
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              + Add
            </Button>
          </form>
          {q.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : cats.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No topics yet. Courses without a topic still list under &quot;All topics&quot;.
            </p>
          ) : (
            <ul className="divide-y">
              {rows.map(({ cat: c, depth, siblings }) => (
                <TopicRow
                  key={c.id}
                  category={c}
                  depth={depth}
                  roots={roots}
                  first={siblings.indexOf(c) === 0}
                  last={siblings.indexOf(c) === siblings.length - 1}
                  onMove={(dir) => move(siblings, c, dir)}
                  onSave={(patch) => update.mutate({ id: c.id, patch })}
                  onPickImage={(file) => setImage.mutate({ id: c.id, file })}
                  onRemoveImage={() => clearImage.mutate(c.id)}
                  imageBusy={
                    (setImage.isPending && setImage.variables?.id === c.id) ||
                    (clearImage.isPending && clearImage.variables === c.id)
                  }
                  onToggleActive={() => update.mutate({ id: c.id, patch: { active: !c.active } })}
                  onDelete={() => {
                    // Deleting a PARENT is not a cascade: the self-FK is ON
                    // DELETE SET NULL, so its sub-topics survive as top-level
                    // topics and keep every course they hold.
                    const hasKids = cats.some((k) => k.parentId === c.id);
                    const warning = hasKids
                      ? ' Its sub-topics are not deleted — they become top-level topics, keeping their courses.'
                      : '';
                    if (
                      confirm(`Delete topic "${c.name}"? Courses in it become untagged.${warning}`)
                    ) {
                      remove.mutate(c.id);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Parent picker. Offers ROOT topics only — a sub-topic as a parent is the
 * `category_parent_not_root` 400, and not offering it is cheaper than
 * explaining it.
 */
function ParentSelect({
  value,
  roots,
  onChange,
  exclude,
  disabled,
  ariaLabel,
}: {
  value: string;
  roots: CourseCategory[];
  onChange: (v: string) => void;
  exclude?: string;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9 w-56" aria-label={ariaLabel}>
        <SelectValue placeholder="— None (top level) —" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_PARENT}>— None (top level) —</SelectItem>
        {roots
          .filter((r) => r.id !== exclude)
          .map((r) => (
            <SelectItem key={r.id} value={r.id}>
              {r.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}

function TopicRow({
  category,
  depth,
  roots,
  first,
  last,
  onMove,
  onSave,
  onToggleActive,
  onDelete,
  onPickImage,
  onRemoveImage,
  imageBusy,
}: {
  category: CourseCategory;
  depth: 0 | 1;
  roots: CourseCategory[];
  first: boolean;
  last: boolean;
  onMove: (dir: -1 | 1) => void;
  onSave: (patch: Partial<CourseCategory>) => void;
  onToggleActive: () => void;
  onDelete: () => void;
  onPickImage: (file: File) => void;
  onRemoveImage: () => void;
  imageBusy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(category.name);
  const [parentId, setParentId] = useState<string>(category.parentId ?? NO_PARENT);

  if (editing) {
    return (
      <li className="py-2" style={{ paddingLeft: depth * 24 }}>
        <form
          className="flex flex-wrap items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const nextParent = parentId === NO_PARENT ? null : parentId;
            // One PATCH carrying only what actually moved — sending an
            // unchanged parentId would re-run the server's nesting checks and
            // could 400 on a row nobody was re-parenting.
            const patch: Partial<CourseCategory> = {};
            if (value.trim() && value.trim() !== category.name) patch.name = value.trim();
            if (nextParent !== (category.parentId ?? null)) patch.parentId = nextParent;
            if (Object.keys(patch).length > 0) onSave(patch);
            setEditing(false);
          }}
        >
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-8 w-56"
            autoFocus
          />
          <ParentSelect
            value={parentId}
            roots={roots}
            exclude={category.id}
            onChange={setParentId}
            ariaLabel={`Parent topic for ${category.name}`}
          />
          <Button type="submit" size="sm">
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setValue(category.name);
              setParentId(category.parentId ?? NO_PARENT);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-2 py-2" style={{ paddingLeft: depth * 24 }}>
      <div className="flex flex-col">
        <button
          type="button"
          className="text-xs leading-none text-muted-foreground disabled:opacity-30"
          disabled={first}
          onClick={() => onMove(-1)}
          aria-label="Move up"
        >
          ▲
        </button>
        <button
          type="button"
          className="text-xs leading-none text-muted-foreground disabled:opacity-30"
          disabled={last}
          onClick={() => onMove(1)}
          aria-label="Move down"
        >
          ▼
        </button>
      </div>
      {depth === 1 && (
        <span aria-hidden className="text-muted-foreground">
          ↳
        </span>
      )}
      {/* Only ROOT topics get a card (and therefore a picture) on the public
          course pages — a sub-topic is a filter pill, not a tile. */}
      {depth === 0 && (
        <TopicImage
          category={category}
          busy={imageBusy}
          onPick={onPickImage}
          onRemove={onRemoveImage}
        />
      )}
      <span className="text-sm font-medium">{category.name}</span>
      {depth === 1 && <span className="text-xs text-muted-foreground">sub-topic</span>}
      {!category.active && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Hidden</span>
      )}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={onToggleActive}>
          {category.active ? 'Hide' : 'Show'}
        </Button>
        <Button variant="outline" size="sm" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </li>
  );
}

/**
 * A root topic's picture: the photo on its "Browse by topic" card.
 *
 * Optional by design — a topic with none falls back to the cover of its first
 * course, and then to the striped placeholder, so a card is never blank. The
 * preview is cache-busted with the stored key because the URL is addressed by
 * topic id and never changes when the image does.
 */
function TopicImage({
  category,
  busy,
  onPick,
  onRemove,
}: {
  category: CourseCategory;
  busy: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
}) {
  const inputId = `topic-image-${category.id}`;
  return (
    <div className="flex items-center gap-1.5">
      <label
        htmlFor={inputId}
        title={category.imageKey ? 'Replace picture' : 'Add a picture'}
        className="flex h-8 w-12 cursor-pointer items-center justify-center overflow-hidden rounded border bg-muted text-[10px] text-muted-foreground hover:border-primary"
      >
        {category.imageKey ? (
          <img
            src={`${courseCategoryImageUrl(category.id)}?v=${encodeURIComponent(category.imageKey)}`}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span>{busy ? '…' : 'photo'}</span>
        )}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        disabled={busy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onPick(file);
        }}
      />
      {category.imageKey && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40"
          title="Remove picture"
        >
          clear
        </button>
      )}
    </div>
  );
}
